import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from "./googleMaps";

/**
 * Load the Maps JS API, once, without `@react-google-maps/api`.
 *
 * That library is ~30kB gzip and it lives in the app's own lazy chunks, where
 * the cost is fair: those surfaces render maps. The address field does not — it
 * needs `places.SearchBox` and `Geocoder` and nothing else — and it is embedded
 * in the marketing pages, which are in the main bundle. Importing the library
 * for it put 32kB on every first visit to the homepage.
 *
 * Binding `SearchBox` to an input imperatively also avoids a remount that no
 * amount of code-splitting would fix: `StandaloneSearchBox` wraps its children
 * in a div of its own, so mounting it *after* a focus (which is when we want
 * the script) moves the input to a new position in the tree and React rebuilds
 * it, taking the focus that triggered the load with it.
 *
 * Memoised on the promise, not on a boolean, so two fields mounting in the same
 * tick share one request rather than racing to inject two script tags. A
 * rejection is not cached: a failed load is usually the network, and the next
 * focus deserves a fresh attempt.
 */
const SCRIPT_ID = "zatoplan-google-maps";

let pending = null;

export const loadGoogleMaps = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  // Already there — including when a surface using useLoadScript got there first.
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (pending) return pending;
  if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error("no Maps API key"));

  pending = new Promise((resolve, reject) => {
    const done = () => {
      if (window.google?.maps?.places) resolve(window.google.maps);
      else reject(new Error("Maps loaded without the places library"));
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", done, { once: true });
      existing.addEventListener("error", () => reject(new Error("Maps failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
      `&libraries=${GOOGLE_MAPS_LIBRARIES.join(",")}`;
    script.addEventListener("load", done, { once: true });
    script.addEventListener("error", () => reject(new Error("Maps failed to load")), {
      once: true,
    });
    document.head.appendChild(script);
  }).catch((err) => {
    pending = null;
    throw err;
  });

  return pending;
};

export default loadGoogleMaps;
